import type { VendasPorMes } from "../dashboard/bento/periodo";
import { PROPOSTAS_ABERTAS } from "./seguradoras";

/**
 * As contas do Dashboard de Seguros (03/11/2026), no molde do Dashboard do
 * Consórcio — puras, sem React nem banco, para dar para testar a régua.
 *
 * A régua de PRODUÇÃO é o prêmio mensal das apólices que COMEÇARAM a vigência
 * no período (negócio novo), pela data de início — o equivalente da cota
 * confirmada do consórcio. Cancelada depois continua contando no mês em que
 * entrou: o cancelamento aparece na carteira e na persistência, não reescreve
 * a produção de um mês fechado.
 *
 * "Anualizado" é o prêmio mensal × 12, que é como o mercado de vida fala de
 * produção.
 */

export type ApolicePainel = {
  seguradora: string;
  numero: string;
  status: string;
  produto_nome: string | null;
  premio_mensal: number | string | null;
  inicio_vigencia: string | null;
  dias_atraso: number | null;
  faturas_em_atraso: number | null;
  segurado_nome: string | null;
  segurado_cpf_digits: string | null;
  corretor_documento_digits: string | null;
  corretor_nome: string | null;
};

export type PropostaPainel = {
  seguradora: string;
  numero: string;
  status: string;
  premio_mensal: number | string | null;
};

export type ComissaoPainel = {
  competencia: string | null;
  valor_comissao: number | string | null;
  tipo_lancamento: string | null;
};

const num = (v: number | string | null | undefined) => Number(v || 0);

/** 'YYYY-MM' no fuso local — a mesma chave que o periodo.ts usa. */
export const chaveDoMes = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

/**
 * O dia do início de vigência como data LOCAL. O banco grava timestamptz;
 * cortar a string em UTC mudaria de mês a apólice que começa no dia 1 às 0h.
 */
const diaLocal = (iso: string) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
};

export const emCobranca = (a: Pick<ApolicePainel, "status" | "faturas_em_atraso">) =>
  a.status === "em_atraso" || num(a.faturas_em_atraso) > 0;

export type ResumoSeguros = {
  /** Prêmio novo por mês (todas as apólices iniciadas, canceladas inclusive). */
  producaoPorMes: VendasPorMes;
  /** Curva acumulada do mês corrente, do dia 1 até hoje. */
  producaoDiaria: { dia: number; acumulado: number }[];
  /** Prêmio novo do mês anterior até o mesmo dia de hoje (base do badge). */
  mesAnteriorMesmoPeriodo: number;
  vigentes: number;
  carteiraMensal: number;
  emCobranca: number;
  premioEmRisco: number;
  propostasAbertas: number;
  pipelineMensal: number;
  aguardandoPagamento: number;
  /** Comissão líquida (créditos − estornos) da competência corrente e da anterior. */
  comissaoMes: number;
  comissaoMesAnterior: number;
  porSeguradora: { seguradora: string; apolices: number; premio: number }[];
  ranking: { chave: string; nome: string; premio: number; apolices: number }[];
  criticos: ApolicePainel[];
};

export const resumirSeguros = (
  apolices: ApolicePainel[],
  propostas: PropostaPainel[],
  comissoes: ComissaoPainel[],
  hoje: Date = new Date(),
): ResumoSeguros => {
  const mesAtual = chaveDoMes(hoje);
  const anterior = chaveDoMes(new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1));
  const diaHoje = hoje.getDate();

  const producaoPorMes: VendasPorMes = {};
  const porDia = new Array<number>(diaHoje + 1).fill(0);
  let mesAnteriorMesmoPeriodo = 0;
  const ranking = new Map<string, { chave: string; nome: string; premio: number; apolices: number }>();
  const porSeguradora = new Map<string, { seguradora: string; apolices: number; premio: number }>();

  let vigentes = 0;
  let carteiraMensal = 0;
  let cobranca = 0;
  let premioEmRisco = 0;

  for (const a of apolices) {
    const premio = num(a.premio_mensal);
    const ativa = a.status === "vigente" || a.status === "em_atraso";
    if (a.status === "vigente") vigentes += 1;
    if (ativa) {
      carteiraMensal += premio;
      const s = porSeguradora.get(a.seguradora) ?? { seguradora: a.seguradora, apolices: 0, premio: 0 };
      s.apolices += 1;
      s.premio += premio;
      porSeguradora.set(a.seguradora, s);
    }
    if (emCobranca(a) && a.status !== "cancelada") {
      cobranca += 1;
      premioEmRisco += premio;
    }

    const inicio = a.inicio_vigencia ? diaLocal(a.inicio_vigencia) : null;
    if (!inicio || inicio > hoje) continue;
    const chave = chaveDoMes(inicio);
    const m = producaoPorMes[chave] ?? { valor: 0, count: 0 };
    m.valor += premio;
    m.count += 1;
    producaoPorMes[chave] = m;

    if (chave === mesAtual) {
      porDia[inicio.getDate()] += premio;
      const quem = a.corretor_documento_digits || a.corretor_nome || "—";
      const r = ranking.get(quem) ?? { chave: quem, nome: a.corretor_nome || "Sem corretor", premio: 0, apolices: 0 };
      r.premio += premio;
      r.apolices += 1;
      ranking.set(quem, r);
    } else if (chave === anterior && inicio.getDate() <= diaHoje) {
      mesAnteriorMesmoPeriodo += premio;
    }
  }

  const producaoDiaria: { dia: number; acumulado: number }[] = [];
  let acumulado = 0;
  for (let dia = 1; dia <= diaHoje; dia++) {
    acumulado += porDia[dia];
    producaoDiaria.push({ dia, acumulado });
  }

  const abertas = propostas.filter((p) => PROPOSTAS_ABERTAS.has(p.status));

  // Débito (estorno) entra negativo: o que a corretora recebe de fato.
  const liquidoDe = (competencia: string) =>
    comissoes
      .filter((c) => c.competencia === competencia)
      .reduce((s, c) => s + num(c.valor_comissao) * (c.tipo_lancamento === "DEBITO" ? -1 : 1), 0);

  return {
    producaoPorMes,
    producaoDiaria,
    mesAnteriorMesmoPeriodo,
    vigentes,
    carteiraMensal,
    emCobranca: cobranca,
    premioEmRisco,
    propostasAbertas: abertas.length,
    pipelineMensal: abertas.reduce((s, p) => s + num(p.premio_mensal), 0),
    aguardandoPagamento: abertas.filter((p) => p.status === "aguardando_pagamento").length,
    comissaoMes: liquidoDe(mesAtual),
    comissaoMesAnterior: liquidoDe(anterior),
    porSeguradora: [...porSeguradora.values()].sort((a, b) => b.premio - a.premio),
    ranking: [...ranking.values()].sort((a, b) => b.premio - a.premio),
    criticos: apolices
      .filter((a) => emCobranca(a) && a.status !== "cancelada" && num(a.dias_atraso) >= 30)
      .sort((a, b) => num(b.dias_atraso) - num(a.dias_atraso)),
  };
};
